const statuses = ["pending", "requires_action", "needs_review", "succeeded", "failed", "canceled"];
const record = value => !!value && typeof value === "object" && !Array.isArray(value);
const identifier = value => typeof value === "string" && value.trim() === value && value.length > 0;
const nullableIdentifier = value => value === null || identifier(value);

export function refundObservationRequest(pending, scope) {
	if (!record(pending) || !record(scope) ||
		![scope.firebaseProject, scope.restaurantId, scope.orderId].every(identifier) ||
		pending.orderId !== scope.orderId || !identifier(pending.requestId) ||
		!Number.isSafeInteger(pending.amountCents) || pending.amountCents <= 0 ||
		(pending.paymentId !== undefined && !nullableIdentifier(pending.paymentId)) ||
		["firebaseProject", "restaurantId"].some(field => pending[field] !== undefined && pending[field] !== scope[field])) {
		throw new Error("Saved refund request scope requires review. Reload the original order.");
	}
	return { firebaseProject: scope.firebaseProject, restaurantId: scope.restaurantId,
		orderId: scope.orderId, requestId: pending.requestId };
}

export function validateRefundObservation(result, request, pending) {
	if (!record(result) || result.success !== true || result.observationOnly !== true ||
		result.source !== "recorded_command" || Object.keys(request).some(field => result[field] !== request[field]) ||
		!identifier(result.refundId) || typeof result.found !== "boolean" ||
		typeof result.effectsComplete !== "boolean" || typeof result.accountingSettled !== "boolean" ||
		!nullableIdentifier(result.stripeRefundId) || !nullableIdentifier(result.paymentId) ||
		!Number.isSafeInteger(result.observedAtMs) || result.observedAtMs < 0) {
		throw new Error("Recorded refund response could not be verified.");
	}
	if (result.found) {
		if (!statuses.includes(result.status) || result.amountCents !== pending.amountCents ||
			result.paymentId !== (pending.paymentId ?? null) ||
			(result.effectsComplete && (result.status !== "succeeded" || !result.accountingSettled))) {
			throw new Error("Recorded refund identity or state requires review.");
		}
	} else if (result.status !== "not_found" || result.amountCents !== null || result.paymentId !== null ||
		result.stripeRefundId !== null || result.effectsComplete || result.accountingSettled) {
		throw new Error("Missing refund response could not be verified.");
	}
	return result;
}

export function validateRefundSubmission(result) {
	if (!record(result) || result.success !== true || !identifier(result.refundId) ||
		!statuses.includes(result.status) || typeof result.effectsComplete !== "boolean" ||
		!nullableIdentifier(result.stripeRefundId) ||
		(result.accountingSettled !== undefined && typeof result.accountingSettled !== "boolean") ||
		(result.effectsComplete && (result.status !== "succeeded" || result.accountingSettled === false))) {
		throw new Error("Refund command response could not be verified.");
	}
	return result;
}

export const refundResultComplete = result => result.status === "succeeded"
	? result.effectsComplete === true && result.accountingSettled !== false
	: ["failed", "canceled"].includes(result.status) && result.accountingSettled === true;

export function recordedRefundMessage(result) {
	if (result.found === false) return "No recorded refund was found for this request. Outcome remains unknown; do not submit another refund.";
	if (result.status === "succeeded") return result.effectsComplete
		? "Recorded refund succeeded; accounting is complete."
		: "Recorded customer refund succeeded; accounting reconciliation remains pending. Do not submit another refund.";
	if (["failed", "canceled"].includes(result.status)) return result.accountingSettled
		? `Recorded refund ${result.status}; accounting is settled.`
		: `Recorded refund ${result.status}; accounting reconciliation remains pending. Do not submit another refund.`;
	return `Recorded refund state: ${result.status.replaceAll("_", " ")}. Do not submit another refund.`;
}
